'use client'

import React from 'react'
import { Collapse, Typography, theme } from 'antd'

import { formatAccordionText } from '@/src/utils'
import faqData from '@/src/data/content/faqData.json'

import type { CollapseProps } from 'antd'

interface AccordionProps {
  accordionType: 'mainAccordion' | 'secondaryAccordions'
}

const { Text, Paragraph } = Typography

const FormattedAnswer: React.FC<{ answer: string }> = ({ answer }) => {
  const {
    token: { colorPrimary },
  } = theme.useToken()

  const paragraphs = answer.split('\n\n')

  return (
    <div style={{ fontSize: 14 }}>
      {paragraphs.map((paragraph, index) => {
        if (paragraph.match(/^\d+\./)) {
          // Numbered list
          const items = paragraph.split('\n')
          return (
            <ol key={index} style={{ paddingLeft: 20 }}>
              {items.map((item, i) => (
                <li key={i}>
                  {formatAccordionText(
                    item.replace(/^\d+\.\s*/, ''),
                    colorPrimary
                  )}
                </li>
              ))}
            </ol>
          )
        } else if (paragraph.match(/^[-•]/)) {
          // Bullet point list
          const items = paragraph.split('\n')
          return (
            <ul key={index} style={{ paddingLeft: 20 }}>
              {items.map((item, i) => {
                const [bulletAndBold, rest] = item.split(/:\s*/, 2)
                const [, ...boldTextParts] = bulletAndBold.split(/^[-•]\s*/)
                const boldText = boldTextParts.join(' ')
                return (
                  <li key={i} style={{ listStyleType: 'disc' }}>
                    <strong>
                      {formatAccordionText(boldText, colorPrimary)}
                    </strong>
                    {rest && ': '}
                    {rest && formatAccordionText(rest, colorPrimary)}
                  </li>
                )
              })}
            </ul>
          )
        }
        return (
          <Paragraph key={index} style={{ fontSize: 14 }}>
            {formatAccordionText(paragraph, colorPrimary)}
          </Paragraph>
        )
      })}
    </div>
  )
}

export const Accordion = ({ accordionType }: AccordionProps) => {
  const mainFaqItems = faqData.slice(0, 1).map((item, index) => ({
    key: `${index}`,
    label: item.question,
    children: item.answer ? <FormattedAnswer answer={item.answer} /> : null,
  }))

  const secondaryFaqItems = faqData.slice(1).map((item, index) => ({
    key: `${index + 1}`,
    label: item.question,
    children: item.answer ? <FormattedAnswer answer={item.answer} /> : null,
  }))

  const items =
    accordionType === 'mainAccordion' ? mainFaqItems : secondaryFaqItems

  const collapseItems: CollapseProps['items'] = items.map((item) => ({
    key: item.key,
    label: (
      <Text style={{ fontSize: 16, fontWeight: 500 }} role='button'>
        {item.label.split(' ').map((word, index) => {
          const cleanWord = word.replace(/[^\w\s]|_/g, '').toLowerCase()
          if (cleanWord === 'physiciangps') {
            const [physicianGPS, punctuation] = word.split(/([\?\!\.])/)
            return (
              <React.Fragment key={index}>
                <span
                  aria-labelledby={`physician-gps-title-${index}`}
                  style={{
                    fontStyle: 'italic',
                    fontWeight: 700,
                  }}
                  className='text-primary'
                >
                  {physicianGPS}
                </span>
                {punctuation || ' '}
              </React.Fragment>
            )
          }
          return word + ' '
        })}
      </Text>
    ),
    children: item.children,
  }))

  return (
    <>
      {collapseItems.map((item, index) => (
        <Collapse
          key={item.key}
          collapsible='header'
          defaultActiveKey={
            accordionType === 'mainAccordion' && index === 0
              ? [item.key as string]
              : []
          }
          items={[item]}
          style={{ marginBottom: 16 }}
        />
      ))}
    </>
  )
}
